import type { HeadGaze } from './face'
import type { ExpressionId } from './expressions'
import { clamp, loopNoise, easings } from './math'

/**
 * Customiser morph choreography helpers: anticipation squash, gaze lead-in,
 * expression accents and blink curves. Expression blend staging lives in
 * `expressions.ts` (`exprMorphBlendT`).
 */
export function anticipationSquash(now: number, at: number, dur = 0.11): number {
  const k = clamp((now - at) / dur)
  if (k >= 1) return 1
  return 1 - Math.sin(k * Math.PI) * 0.028
}

/** Gaze creeps toward the new expression before the morph fully commits. */
export function gazeAnticipationDelta(
  from: HeadGaze,
  to: HeadGaze,
  now: number,
  at: number,
  dur = 0.22
): HeadGaze {
  const k = clamp((now - at) / dur)
  if (k >= 1) return { yaw: 0, pitch: 0, roll: 0 }
  const lead = easings.easeOutCubic(k) * 0.26
  return {
    yaw: (to.yaw - from.yaw) * lead,
    pitch: (to.pitch - from.pitch) * lead,
    roll: (to.roll - from.roll) * lead
  }
}

/** Forced-blink curve for tests and optional choreography: close, hold, reopen. */
export function forcedBlinkLid(now: number, at: number, dur = 0.5): number {
  const k = clamp((now - at) / dur)
  if (k >= 1) return 1
  if (k < 0.05) return 1
  if (k < 0.15) return 1 - ((k - 0.05) / 0.1) * 0.94
  if (k < 0.55) return 0.06
  return 0.06 + ((k - 0.55) / 0.45) * 0.94
}

export interface MotionAccent {
  offX: number
  offY: number
  dRoll: number
  breath: number
  /** Vertical eye squash — hilare laugh beat only; 1 = fully open. */
  lid: number
}

const ACCENT_NONE: MotionAccent = { offX: 0, offY: 0, dRoll: 0, breath: 1, lid: 1 }

/**
 * Laugh cadence: eyes squeeze on each beat, body dips with the ha then springs back.
 * Pure function of t — wired in the engine, not the eyefit table.
 */
export function laughPulse(t: number): Pick<MotionAccent, 'offY' | 'breath' | 'lid'> {
  const phase = t * 8.2
  const beat = Math.abs(Math.sin(phase))
  const signed = Math.sin(phase)
  const lid = 0.16 + 0.84 * (1 - beat * beat)
  return {
    lid,
    offY: signed * 0.014,
    breath: 1 + beat * 0.018
  }
}

/** Expression-specific body accents — offsets only, never eyefit. */
export function expressionAccent(id: ExpressionId | null, t: number): MotionAccent {
  if (!id) return ACCENT_NONE

  switch (id) {
    case 'hilare': {
      const p = laughPulse(t)
      return {
        offX: 0,
        offY: p.offY,
        dRoll: Math.sin(t * 8.2) * 0.38,
        breath: p.breath,
        lid: p.lid
      }
    }
    case 'heureux':
    case 'excite':
    case 'fier':
      return {
        offX: 0,
        offY: Math.sin(t * 9.1) * 0.005 + Math.sin(t * 4.3) * 0.002,
        dRoll: Math.sin(t * 5.7) * 0.45,
        breath: 1 + Math.sin(t * 4.3) * 0.007,
        lid: 1
      }
    case 'effraye':
    case 'mefiant': {
      const shake = loopNoise(t, 0.11, 31)
      return {
        offX: shake * 0.008,
        offY: loopNoise(t, 0.13, 19) * 0.003,
        dRoll: shake * 0.65,
        breath: 1,
        lid: 1
      }
    }
    case 'surpris':
      return {
        offX: 0,
        offY: Math.sin(t * 12) * 0.003 * Math.max(0, Math.sin(t * 0.7)),
        dRoll: 0,
        breath: 1 + Math.sin(t * 6) * 0.005,
        lid: 1
      }
    case 'somnolent':
    case 'blase':
      return {
        offX: loopNoise(t, 4.2, 8) * 0.004,
        offY: Math.sin(t * 1.8) * 0.003,
        dRoll: loopNoise(t, 5.1, 3) * 0.55,
        breath: 1 + Math.sin(t * 2.2) * 0.004,
        lid: 1
      }
    case 'colere':
    case 'triste':
      return {
        offX: 0,
        offY: Math.sin(t * 3.5) * 0.002,
        dRoll: loopNoise(t, 2.8, 11) * 0.35,
        breath: 1,
        lid: 1
      }
    default:
      return {
        offX: loopNoise(t, 6.3, 22) * 0.003,
        offY: loopNoise(t, 4.1, 17) * 0.002,
        dRoll: loopNoise(t, 7.2, 9) * 0.28,
        breath: 1,
        lid: 1
      }
  }
}
